import { FC } from 'react';
import { MediaAvatar } from 'elements/Avatar';
import { User } from 'models/User';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import classNames from 'classnames';
import ActionMenu from 'elements/ActionMenu';
import { useAccountMenuItems } from 'hooks/useAccountMenuItems';
import { useTranslations } from 'next-intl';

export type UserButtonVariant = 'default' | 'condensed';
export type UserButtonTone = 'light' | 'dark';

interface Props {
  // Undefined until the user has been read on the client; the button then
  // shows a skeleton of the same size, so nothing around it moves.
  user?: User;
  isPro?: boolean;
  /**
   * `default` shows the avatar plus name/username and a chevron; `condensed`
   * shows only the avatar. The caller decides which to use (e.g. based on
   * layout) — this component isn't coupled to viewport size.
   */
  variant?: UserButtonVariant;
  tone?: UserButtonTone;
}

const UserButton: FC<Props> = ({
  user,
  isPro,
  variant = 'default',
  tone = 'light',
}) => {
  const t = useTranslations();
  const items = useAccountMenuItems();
  const condensed = variant === 'condensed';
  // Until we know the user and have their photo, pulse like the name below
  // rather than flash a generic icon or the initials.
  const avatarSkeleton = <span className={styles.avatarSkeleton(tone)} />;

  return (
    <ActionMenu
      className="relative flex"
      triggerClassName={styles.trigger}
      triggerAriaLabel={t('accountMenu.title')}
      anchor="bottom end"
      // Drop the menu clear of the avatar's ring and chevron badge, which
      // hang below the button; the default gap tucks it under them.
      panelClassName="[--anchor-gap:--spacing(3)]!"
      items={items}
      trigger={({ open }) => (
        <>
          {/* The chevron sits on the avatar as a small badge, so the button
              has the same shape with or without a name beside it. The wrapper
              is a flex box, not inline, so the avatar isn't placed on a text
              line whose baseline moves when the image replaces the icon. */}
          <span className="relative flex flex-none">
            <MediaAvatar
              size="sm"
              mediaId={user?.profileImage?.id}
              initials={user?.getInitials()}
              colorKey={user?.id}
              icon={user ? undefined : avatarSkeleton}
              placeholder={avatarSkeleton}
              variant="outline"
              outlineColorClass={styles.outline(tone)}
              gradientRing={isPro}
            />
            <span className={styles.badge(tone)} aria-hidden>
              <FontAwesomeIcon
                icon={faChevronDown}
                className={classNames(styles.chevron, open && 'rotate-180')}
              />
            </span>
          </span>

          {/* Fixed width so the button is the same size for every name: a
              long one trails off, and the skeleton shown before the user is
              known takes the same room. */}
          {!condensed && (
            <div className={styles.userInfo}>
              {user ? (
                <>
                  <span className={styles.name(tone)}>{user.displayName}</span>
                  <span className={styles.username(tone)}>
                    {user.getUsername()}
                  </span>
                </>
              ) : (
                <span className={styles.skeleton} aria-hidden>
                  <span className={styles.skeletonName(tone)} />
                  <span className={styles.skeletonUsername(tone)} />
                </span>
              )}
            </div>
          )}
        </>
      )}
    />
  );
};

export default UserButton;

const styles = {
  trigger: 'flex flex-row items-center gap-3 select-none focus:outline-none',
  outline: (tone: UserButtonTone) =>
    tone === 'dark' ? 'outline-white/40' : 'outline-mf-blue-800',
  // Sits on the avatar's outer ring at the bottom-right (the ring is 5px
  // outside the photo), same ring colour, filled with the surface the button
  // sits on, so it reads as part of the avatar.
  badge: (tone: UserButtonTone) =>
    classNames(
      'absolute -bottom-1.5 -right-1.5 flex h-[18px] w-[18px] items-center justify-center',
      'rounded-full border',
      tone === 'dark'
        ? 'border-white/40 bg-mf-blue-800 text-white/80'
        : 'border-mf-blue-800/40 bg-white text-[#6C727E]',
    ),
  chevron: 'text-[8px] transition-transform duration-150',
  userInfo: 'flex flex-col justify-center w-36 text-left',
  name: (tone: UserButtonTone) =>
    `block font-semibold text-base leading-tight truncate ${
      tone === 'dark' ? 'text-white' : ''
    }`,
  username: (tone: UserButtonTone) =>
    `block text-sm font-normal leading-tight truncate ${
      tone === 'dark' ? 'text-white/60' : 'text-gray-500'
    }`,
  avatarSkeleton: (tone: UserButtonTone) =>
    `block h-full w-full animate-pulse ${tone === 'dark' ? 'bg-white/30' : 'bg-gray-200'}`,
  skeleton: 'flex flex-col gap-1.5 py-1 animate-pulse',
  skeletonName: (tone: UserButtonTone) =>
    `block h-3.5 w-28 rounded ${tone === 'dark' ? 'bg-white/30' : 'bg-gray-200'}`,
  skeletonUsername: (tone: UserButtonTone) =>
    `block h-3 w-20 rounded ${tone === 'dark' ? 'bg-white/20' : 'bg-gray-100'}`,
};
